import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import * as MenubarPrimitive from '@radix-ui/react-menubar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  Menubar,
  MenubarItem,
  MenubarLabel,
  MenubarMenu,
  MenubarSeparator,
  MenubarSub,
  MenubarSubTrigger,
  MenubarTrigger,
} from '../ui/menubar';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { useToast } from '../ui/use-toast';
import {
  BarChart3,
  Brain,
  ClipboardList,
  Cloud,
  FileText,
  FlaskConical,
  FolderOpen,
  Gauge,
  GitBranch,
  HelpCircle,
  Home,
  Info,
  LayoutGrid,
  LineChart,
  LucideIcon,
  Monitor,
  Network,
  PanelLeftClose,
  PanelLeftOpen,
  PieChart,
  RefreshCcw,
  Save,
  Search,
  Settings2,
  ShieldCheck,
  Sigma,
  Table2,
  TestTube,
  TimerReset,
  TrendingUp,
  Upload,
  Workflow,
} from 'lucide-react';

import { useAuth } from '../../hooks/useAuth';
import { useUserVFS } from '../../hooks/useUserVFS';
import { globalVFS } from '../../utils/storage/VFS';
import { SaveAsDialog } from './SaveAsDialog';
import { DescriptiveStats } from './stats/DescriptiveStats';
import { HypothesisTesting } from './stats/HypothesisTesting';
import { RegressionAnalysis } from './stats/RegressionAnalysis';
import { DistributionAnalysis } from './stats/DistributionAnalysis';
import { DataVisualization } from './stats/DataVisualization';
import { CapabilityControlCharts } from './stats/CapabilityControlCharts';
import { DOEOptimizer } from './stats/DOEOptimizer';
import { TimeSeriesForecasting } from './stats/TimeSeriesForecasting';
import { AdvancedGraphBuilder } from './stats/AdvancedGraphBuilder';
import { MeasurementSystemsAnalysis } from './stats/MeasurementSystemsAnalysis';
import { PredictiveAnalytics } from './stats/PredictiveAnalytics';
import { MultivariateAnalysis } from './stats/MultivariateAnalysis';
import { ReliabilitySurvival } from './stats/ReliabilitySurvival';
import { NonparametricsAnalysis } from './stats/NonparametricsAnalysis';
import { TablesCategoricalAnalysis } from './stats/TablesCategoricalAnalysis';
import { MapleStatsDataSheet } from './stats/MapleStatsDataSheet';
import { MapleStatsAiAssistant } from './stats/MapleStatsAiAssistant';
import { ParsedWorksheet, parseWorksheet } from './stats/worksheet';

interface MapleStatsProps {
  filePath?: string;
  initialContent?: string;
  onClose?: () => void;
}

type ModuleStatus = 'ready' | 'beta' | 'planned';
type WorkspaceTab = 'solutions' | 'data';

type ModuleId =
  | 'home'
  | 'graph-builder'
  | 'descriptive'
  | 'hypothesis'
  | 'regression'
  | 'distributions'
  | 'visualization'
  | 'capability-control'
  | 'doe-optimizer'
  | 'time-series'
  | 'measurement-systems'
  | 'reliability'
  | 'predictive-analytics'
  | 'multivariate'
  | 'nonparametrics'
  | 'tables'
  | 'automation';

interface WorkspaceModule {
  id: ModuleId;
  name: string;
  description: string;
  group: string;
  icon: LucideIcon;
  status: ModuleStatus;
  available: boolean;
  accent: string;
  capabilities: string[];
}

interface MapleStatsSolutionFile {
  app: 'MapleStats';
  version: 1;
  title: string;
  savedAt: string;
  workspace: {
    activeModuleId: ModuleId;
    activeWorkspaceTab: WorkspaceTab;
    dataInput: string;
    delimiterMode: string;
    selectedColumnId: string;
    moduleSearch: string;
    solutionNotes?: string;
  };
}

interface MapleSheetCell {
  value?: string;
  formula?: string;
}

const MapleStatsMenubarContent = React.forwardRef<
  React.ElementRef<typeof MenubarPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof MenubarPrimitive.Content>
>(({ className = '', align = 'start', alignOffset = -4, sideOffset = 6, ...props }, ref) => (
  <MenubarPrimitive.Content
    ref={ref}
    align={align}
    alignOffset={alignOffset}
    sideOffset={sideOffset}
    className={`z-[10000] min-w-[12rem] overflow-hidden rounded-md border border-border bg-popover p-1 text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 ${className}`.trim()}
    {...props}
  />
));
MapleStatsMenubarContent.displayName = 'MapleStatsMenubarContent';

const MapleStatsMenubarSubContent = React.forwardRef<
  React.ElementRef<typeof MenubarPrimitive.SubContent>,
  React.ComponentPropsWithoutRef<typeof MenubarPrimitive.SubContent>
>(({ className = '', sideOffset = 6, ...props }, ref) => (
  <MenubarPrimitive.SubContent
    ref={ref}
    sideOffset={sideOffset}
    className={`z-[10000] min-w-[14rem] overflow-hidden rounded-md border border-border bg-popover p-1 text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 ${className}`.trim()}
    {...props}
  />
));
MapleStatsMenubarSubContent.displayName = 'MapleStatsMenubarSubContent';

const DEFAULT_SOLUTION_NAME = 'Untitled Solution';
const MAPLESTATS_FILE_EXTENSION = '.mstats';
const MAPLESTATS_CONTENT_TYPE = 'application/x-maplestats+json';
const MAPLESTATS_RECENT_SOLUTIONS_KEY = 'maplestats_recent_solutions';
const MAX_RECENT_SOLUTIONS = 8;
const DEMO_TEMPERATURE_DATA = [
  'Sample,Temperature_C,Pressure_kPa,Humidity_pct,Runtime_min,Energy_kWh',
  'A1,18.4,101.2,42,35,6.2',
  'A2,19.1,100.8,44,36,6.5',
  'A3,20.3,100.5,47,39,6.9',
  'A4,21.0,100.1,49,41,7.1',
  'A5,22.6,99.8,52,44,7.6',
  'A6,23.4,99.4,55,46,7.9',
  'A7,24.2,99.0,58,49,8.3',
  'A8,25.1,98.7,60,51,8.7',
].join('\n');

const MODULES: WorkspaceModule[] = [
  {
    id: 'home',
    name: 'Solution Center',
    description: 'Launch worksheet-driven workflows and review which analysis families are ready for your current data.',
    group: 'Start',
    icon: Home,
    status: 'ready',
    available: true,
    accent: 'bg-slate-700',
    capabilities: ['Worksheet-first analysis', 'Quick launch tiles', 'Readiness by module family'],
  },
  {
    id: 'graph-builder',
    name: 'Graph Builder',
    description: 'Build advanced histograms, scatterplots, bubble plots, Pareto charts, heatmaps, run charts, and boxplots.',
    group: 'Start',
    icon: Sigma,
    status: 'ready',
    available: true,
    accent: 'bg-violet-600',
    capabilities: ['Histogram and run chart', 'Scatter and bubble plots', 'Pareto and heatmap workflows'],
  },
  {
    id: 'descriptive',
    name: 'Descriptive Statistics',
    description: 'Compute summary measures, intervals, skewness, kurtosis, and variability metrics.',
    group: 'Core Analysis',
    icon: BarChart3,
    status: 'ready',
    available: true,
    accent: 'bg-blue-600',
    capabilities: ['Central tendency', 'Spread and quartiles', 'Confidence interval for mean'],
  },
  {
    id: 'hypothesis',
    name: 'Hypothesis Testing',
    description: 'Run configurable one-sample and two-sample inference from selected worksheet data.',
    group: 'Core Analysis',
    icon: TestTube,
    status: 'ready',
    available: true,
    accent: 'bg-emerald-600',
    capabilities: ['One-sample t and Z tests', 'Two-sample t test', 'Alternative hypothesis control'],
  },
  {
    id: 'regression',
    name: 'Regression Analysis',
    description: 'Analyze linear and polynomial relationships with residual diagnostics and fit summaries.',
    group: 'Core Analysis',
    icon: TrendingUp,
    status: 'ready',
    available: true,
    accent: 'bg-orange-600',
    capabilities: ['Linear regression', 'Polynomial regression', 'R² and residual metrics'],
  },
  {
    id: 'distributions',
    name: 'Probability Distributions',
    description: 'Explore common statistical distributions and probability-oriented analysis views.',
    group: 'Core Analysis',
    icon: PieChart,
    status: 'ready',
    available: true,
    accent: 'bg-fuchsia-600',
    capabilities: ['Normal and t distribution', 'Chi-square and F views', 'Distribution summaries'],
  },
  {
    id: 'visualization',
    name: 'Graphical Analysis',
    description: 'Use the original single-column charting view for histograms, density overlays, and summary displays.',
    group: 'Core Analysis',
    icon: LineChart,
    status: 'ready',
    available: true,
    accent: 'bg-cyan-600',
    capabilities: ['Histogram', 'Density overlay', 'Summary table'],
  },
  {
    id: 'capability-control',
    name: 'Capability + Control Charts',
    description: 'Evaluate Cp / Cpk / Pp / Ppk against spec limits and monitor stability with I-MR charts.',
    group: 'Quality & Process',
    icon: Gauge,
    status: 'ready',
    available: true,
    accent: 'bg-rose-600',
    capabilities: ['Capability metrics', 'Histogram vs specs', 'Individuals and moving range charts'],
  },
  {
    id: 'measurement-systems',
    name: 'Measurement Systems Analysis',
    description: 'Run a Type 1 gage check or a crossed-study screening workflow from worksheet columns.',
    group: 'Quality & Process',
    icon: ClipboardList,
    status: 'ready',
    available: true,
    accent: 'bg-amber-600',
    capabilities: ['Type 1 gage study', 'Crossed study screening', 'Bias, repeatability, GRR estimates'],
  },
  {
    id: 'doe-optimizer',
    name: 'DOE + Response Optimizer',
    description: 'Generate 2-level factorial designs, fit coded models, and find the best predicted corner.',
    group: 'Design & Optimization',
    icon: GitBranch,
    status: 'ready',
    available: true,
    accent: 'bg-indigo-600',
    capabilities: ['2 and 3 factor designs', 'Manual or worksheet-fitted coded model', 'Corner response optimization'],
  },
  {
    id: 'time-series',
    name: 'Time Series + Forecasting',
    description: 'Forecast sequential data with moving averages, exponential smoothing, and linear trend models.',
    group: 'Forecasting & AI',
    icon: TimerReset,
    status: 'ready',
    available: true,
    accent: 'bg-blue-700',
    capabilities: ['Moving average', 'Exponential smoothing', 'Trend forecast horizon'],
  },
  {
    id: 'predictive-analytics',
    name: 'Predictive Analytics',
    description: 'Compare quick worksheet-driven predictive models with holdout scoring for regression and classification tasks.',
    group: 'Forecasting & AI',
    icon: Brain,
    status: 'ready',
    available: true,
    accent: 'bg-purple-700',
    capabilities: ['Auto task detection', 'Baseline, linear, centroid, and tree-style model comparison', 'Holdout metrics and prediction preview'],
  },
  {
    id: 'multivariate',
    name: 'Multivariate',
    description: 'Explore PCA structure, variable relationships, and k-means clustering directly from worksheet columns.',
    group: 'Forecasting & AI',
    icon: Network,
    status: 'ready',
    available: true,
    accent: 'bg-teal-700',
    capabilities: ['PCA score and loading views', 'Correlation matrix heatmap', 'K-means clustering with centroid summaries'],
  },
  {
    id: 'reliability',
    name: 'Reliability / Survival',
    description: 'Analyze life data with survival curves, Weibull fitting, warranty screens, Weibayes estimates, and stress-life views.',
    group: 'Specialized Methods',
    icon: ShieldCheck,
    status: 'ready',
    available: true,
    accent: 'bg-green-700',
    capabilities: ['Kaplan-Meier survival analysis', 'Weibull, warranty, and Weibayes screens', 'Accelerated stress-life trend view'],
  },
  {
    id: 'nonparametrics',
    name: 'Nonparametrics',
    description: 'Run rank-based and distribution-free tests for independent samples, paired samples, grouped data, medians, and sequence randomness.',
    group: 'Specialized Methods',
    icon: Workflow,
    status: 'ready',
    available: true,
    accent: 'bg-violet-700',
    capabilities: ['Mann-Whitney and Wilcoxon signed-rank', 'Kruskal-Wallis', 'Sign and runs tests'],
  },
  {
    id: 'tables',
    name: 'Tables & Categorical Analysis',
    description: 'Prepare cross tabs, chi-square workflows, Fisher\'s exact tests, and categorical summaries.',
    group: 'Specialized Methods',
    icon: PieChart,
    status: 'ready',
    available: true,
    accent: 'bg-sky-700',
    capabilities: ['Cross tabulation', 'Chi-square workflows', 'Fisher exact and categorical summaries'],

  },
  {
    id: 'automation',
    name: 'Macros & Customization',
    description: 'Prepare user profiles, menu customization, scripting, and external language integrations.',
    group: 'Specialized Methods',
    icon: Settings2,
    status: 'planned',
    available: false,
    accent: 'bg-amber-700',
    capabilities: ['Profiles and preferences', 'Custom menus', 'Python and R roadmap'],
  },
];

const getStatusBadgeVariant = (status: ModuleStatus): 'default' | 'secondary' | 'outline' => {
  if (status === 'ready') return 'default';
  if (status === 'beta') return 'secondary';
  return 'outline';
};

const normalizePath = (path: string) => path.replace(/\//g, '\\').replace(/\\\\+/g, '\\');

const ensureStatsExtension = (path: string) => {
  const normalized = normalizePath(path);
  return normalized.toLowerCase().endsWith('.mstats') ? normalized : `${normalized}.mstats`;
};

const getSolutionNameFromPath = (path?: string | null) => {
  if (!path) return DEFAULT_SOLUTION_NAME;
  const fileName = path.split('\\').pop() || DEFAULT_SOLUTION_NAME;
  return fileName.replace(/\.mstats$/i, '') || DEFAULT_SOLUTION_NAME;
};

const isValidModuleId = (value: string): value is ModuleId => MODULES.some((module) => module.id === value);

const normalizeWorkspaceTab = (value: string | undefined): WorkspaceTab => value === 'data' ? 'data' : 'solutions';

const mapleSheetColumnToIndex = (letters: string) => {
  return letters
    .toUpperCase()
    .split('')
    .reduce((total, letter) => total * 26 + (letter.charCodeAt(0) - 64), 0) - 1;
};

const toCsvCell = (value: string) => {
  return /[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
};

const serializeMapleSheetToWorksheet = (content: string) => {
  const parsed = JSON.parse(content) as { data?: Record<string, MapleSheetCell> };
  const cells = parsed?.data;

  if (!cells || typeof cells !== 'object') {
    throw new Error('This file is not a valid MapleSheet workbook.');
  }

  let maxRowIndex = -1;
  let maxColumnIndex = -1;
  const grid = new Map<string, string>();

  Object.entries(cells).forEach(([cellId, cell]) => {
    const match = /^([A-Za-z]+)(\d+)$/.exec(cellId);
    if (!match) return;

    const rawValue = typeof cell?.value === 'string'
      ? cell.value
      : typeof cell?.formula === 'string'
        ? cell.formula
        : '';

    if (!rawValue.trim()) return;

    const columnIndex = mapleSheetColumnToIndex(match[1]);
    const rowIndex = Math.max(0, Number.parseInt(match[2], 10) - 1);

    grid.set(`${rowIndex}:${columnIndex}`, rawValue);
    maxRowIndex = Math.max(maxRowIndex, rowIndex);
    maxColumnIndex = Math.max(maxColumnIndex, columnIndex);
  });

  if (maxRowIndex < 0 || maxColumnIndex < 0) {
    return '';
  }

  return Array.from({ length: maxRowIndex + 1 }, (_, rowIndex) => {
    return Array.from({ length: maxColumnIndex + 1 }, (_, columnIndex) => {
      return toCsvCell(grid.get(`${rowIndex}:${columnIndex}`) ?? '');
    }).join(',');
  }).join('\n');
};

const createSolutionPayload = (
  workspace: MapleStatsSolutionFile['workspace'],
  title: string,
  savedAt = new Date().toISOString(),
): MapleStatsSolutionFile => ({
  app: 'MapleStats',
  version: 1,
  title,
  savedAt,
  workspace,
});

const createSolutionComparisonContent = (
  workspace: MapleStatsSolutionFile['workspace'],
  title: string,
) => JSON.stringify(createSolutionPayload(workspace, title, '__comparison__'), null, 2);

const SolutionCenterPanel = ({
  modules,
  worksheet,
  onOpenModule,
  onOpenData,
}: {
  modules: WorkspaceModule[];
  worksheet: ParsedWorksheet;
  onOpenModule: (moduleId: ModuleId) => void;
  onOpenData: () => void;
}) => {
  const quickLaunch = modules.filter((module) => module.available && module.id !== 'home').slice(0, 8);
  const isEmptyWorksheet = worksheet.rowCount === 0;

  const readiness = [
    {
      label: 'Single-column statistics',
      state: worksheet.numericColumns.length >= 1 ? 'Ready' : 'Needs one numeric column',
    },
    {
      label: 'Two-column relationships',
      state: worksheet.numericColumns.length >= 2 ? 'Ready' : 'Needs two numeric columns',
    },
    {
      label: 'DOE and optimization',
      state: worksheet.numericColumns.length >= 3 ? 'Ready for worksheet fitting' : 'Use manual factor model or add more numeric columns',
    },
    {
      label: 'Measurement systems analysis',
      state: worksheet.columns.length >= 3 ? 'Ready for crossed study mapping' : 'Needs part, operator, and measurement columns',
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h2 className="text-3xl font-semibold tracking-tight">Solution Center</h2>
          <p className="mt-2 text-sm text-muted-foreground">Build a fresh MapleStats solution and open the Data tab when you're ready to enter or load worksheet data.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" onClick={onOpenData}>
            <Table2 className="mr-2 h-4 w-4" />
            Open Data Worksheet
          </Button>
        </div>
      </div>

      {isEmptyWorksheet ? (
        <Card className="border-dashed">
          <CardHeader>
            <CardTitle>Start a new solution</CardTitle>
            <CardDescription>
              Your worksheet is empty by default so you can begin with a brand-new solution. Open the Data tab to enter your own data or load the demo worksheet.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-3">
            <Button variant="outline" onClick={onOpenData}>
              <LayoutGrid className="mr-2 h-4 w-4" />
              Go to Data
            </Button>
          </CardContent>
        </Card>
      ) : null}

      <div className="grid gap-4 xl:grid-cols-4">
        {quickLaunch.map((module) => {
          const Icon = module.icon;
          return (
            <button
              key={module.id}
              type="button"
              onClick={() => onOpenModule(module.id)}
              className="rounded-xl border border-border bg-card p-5 text-left transition-colors hover:bg-muted/40"
            >
              <div className="mb-4 flex items-start justify-between gap-3">
                <div className={`rounded-lg p-2 text-white ${module.accent}`}>
                  <Icon size={20} />
                </div>
                <Badge variant={getStatusBadgeVariant(module.status)}>{module.status}</Badge>
              </div>
              <h3 className="text-lg font-semibold">{module.name}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{module.description}</p>
            </button>
          );
        })}
      </div>

    </div>
  );
};

const PlannedModulePanel = ({ module }: { module: WorkspaceModule }) => {
  const Icon = module.icon;

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-4">
        <div className={`rounded-xl p-3 text-white ${module.accent}`}>
          <Icon size={24} />
        </div>
        <div>
          <div className="mb-2 flex items-center gap-2">
            <Badge variant={getStatusBadgeVariant(module.status)}>{module.status}</Badge>
            <Badge variant="outline">Roadmap module</Badge>
          </div>
          <h2 className="text-2xl font-semibold text-foreground">{module.name}</h2>
          <p className="mt-2 max-w-3xl text-muted-foreground">{module.description}</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Planned capabilities</CardTitle>
          <CardDescription>This module remains scaffolded in the workspace and is ready for a deeper implementation phase.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {module.capabilities.map((capability) => (
            <div key={capability} className="rounded-lg border border-border/70 px-4 py-3 text-sm">
              {capability}
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};

export const MapleStats: React.FC<MapleStatsProps> = ({ filePath, initialContent, onClose }) => {
  const { toast } = useToast();
  const { user } = useAuth();
  const { files, isLoaded, createFile, updateFile, getFile, fileExists, reloadFile } = useUserVFS();

  const [activeModuleId, setActiveModuleId] = useState<ModuleId>('home');
  const [activeWorkspaceTab, setActiveWorkspaceTab] = useState<WorkspaceTab>('solutions');
  const [dataInput, setDataInput] = useState('');
  const [delimiterMode, setDelimiterMode] = useState('auto');
  const [moduleSearch, setModuleSearch] = useState('');
  const [selectedColumnId, setSelectedColumnId] = useState('');
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const [currentFilePath, setCurrentFilePath] = useState<string | null>(filePath ?? null);
  const [currentSolutionName, setCurrentSolutionName] = useState(DEFAULT_SOLUTION_NAME);
  const [showSaveAsDialog, setShowSaveAsDialog] = useState(false);
  const [showOpenDialog, setShowOpenDialog] = useState(false);
  const [showOpenSheetDialog, setShowOpenSheetDialog] = useState(false);
  const [showHelpDialog, setShowHelpDialog] = useState(false);
  const [showAboutDialog, setShowAboutDialog] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [lastSavedContent, setLastSavedContent] = useState<string | null>(null);
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);
  const [recentSolutionPaths, setRecentSolutionPaths] = useState<string[]>([]);

  const initialLoadRef = useRef(false);
  const hydratingRef = useRef(false);
  const importInputRef = useRef<HTMLInputElement | null>(null);

  const worksheet = useMemo(() => parseWorksheet(dataInput, delimiterMode), [dataInput, delimiterMode]);
  const activeModule = MODULES.find((module) => module.id === activeModuleId) ?? MODULES[0];
  const ActiveModuleIcon = activeModule.icon;

  useEffect(() => {
    const hasSelectedColumn = worksheet.numericColumns.some((column) => column.id === selectedColumnId);
    if (!hasSelectedColumn) {
      setSelectedColumnId(worksheet.numericColumns[0]?.id ?? '');
    }
  }, [selectedColumnId, worksheet.numericColumns]);

  const selectedColumn = worksheet.numericColumns.find((column) => column.id === selectedColumnId) ?? worksheet.numericColumns[0];
  const selectedData = selectedColumn?.numericValues ?? [];

  const filteredModules = useMemo(() => {
    const search = moduleSearch.trim().toLowerCase();
    if (!search) return MODULES;

    return MODULES.filter((module) => {
      const haystack = [module.name, module.description, module.group, ...module.capabilities].join(' ').toLowerCase();
      return haystack.includes(search);
    });
  }, [moduleSearch]);

  const groupedModules = useMemo(() => {
    return filteredModules.reduce<Record<string, WorkspaceModule[]>>((acc, module) => {
      if (!acc[module.group]) acc[module.group] = [];
      acc[module.group].push(module);
      return acc;
    }, {});
  }, [filteredModules]);

  const availableSolutionFiles = useMemo(() => {
    return [...files]
      .filter((file) => !file.is_directory && file.file_path.toLowerCase().endsWith('.mstats'))
      .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());
  }, [files]);

  const availableMapleSheetFiles = useMemo(() => {
    return [...files]
      .filter((file) => !file.is_directory && file.file_path.toLowerCase().endsWith('.msheet'))
      .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());
  }, [files]);

  const recentSolutions = useMemo(() => {
    return recentSolutionPaths
      .map((path) => {
        const file = availableSolutionFiles.find((entry) => normalizePath(entry.file_path) === normalizePath(path));
        return {
          path,
          title: getSolutionNameFromPath(path),
          updatedAt: file?.updated_at ?? null,
          existsInWorkspace: Boolean(file),
        };
      })
      .filter((entry) => entry.existsInWorkspace || normalizePath(entry.path) === normalizePath(currentFilePath ?? ''));
  }, [availableSolutionFiles, currentFilePath, recentSolutionPaths]);

  const pushRecentSolutionPath = useCallback((path: string | null | undefined) => {

    if (!path) return;
    const normalized = ensureStatsExtension(path);
    setRecentSolutionPaths((previous) => {
      const next = [
        normalized,
        ...previous.filter((entry) => normalizePath(entry) !== normalizePath(normalized)),
      ].slice(0, MAX_RECENT_SOLUTIONS);

      try {
        window.localStorage.setItem(MAPLESTATS_RECENT_SOLUTIONS_KEY, JSON.stringify(next));
      } catch {}

      return next;
    });
  }, []);

  const buildWorkspaceState = useCallback(
    (workspaceTitle: string): MapleStatsSolutionFile =>
      createSolutionPayload(
        {
          activeModuleId,
          activeWorkspaceTab,
          dataInput,
          delimiterMode,
          selectedColumnId,
          moduleSearch,
          solutionNotes: undefined,
        },
        workspaceTitle,
      ),
    [activeModuleId, activeWorkspaceTab, dataInput, delimiterMode, selectedColumnId, moduleSearch],
  );

  const currentSolutionContent = useMemo(() => {
    return createSolutionComparisonContent(
      {
        activeModuleId,
        activeWorkspaceTab,
        dataInput,
        delimiterMode,
        selectedColumnId,
        moduleSearch,
        solutionNotes: undefined,
      },
      currentSolutionName,
    );
  }, [activeModuleId, activeWorkspaceTab, currentSolutionName, dataInput, delimiterMode, moduleSearch, selectedColumnId]);

  const emptySolutionContent = useMemo(() => {
    return createSolutionComparisonContent(
      {
        activeModuleId: 'home',
        activeWorkspaceTab: 'solutions',
        dataInput: '',
        delimiterMode: 'auto',
        selectedColumnId: '',
        moduleSearch: '',
        solutionNotes: undefined,
      },
      currentSolutionName,
    );
  }, [currentSolutionName]);

  const hasUnsavedChanges = currentSolutionContent !== (lastSavedContent ?? emptySolutionContent);

  const applySolutionPayload = useCallback((payload: MapleStatsSolutionFile, path: string | null, savedAt?: string | null) => {
    hydratingRef.current = true;

    setActiveModuleId(payload.workspace.activeModuleId);
    setActiveWorkspaceTab(payload.workspace.activeWorkspaceTab);
    setDataInput(payload.workspace.dataInput);
    setDelimiterMode(payload.workspace.delimiterMode);
    setSelectedColumnId(payload.workspace.selectedColumnId);
    setModuleSearch(payload.workspace.moduleSearch);
    setCurrentFilePath(path);
    setCurrentSolutionName(payload.title || getSolutionNameFromPath(path));
    setLastSavedContent(createSolutionComparisonContent(payload.workspace, payload.title || getSolutionNameFromPath(path)));
    setLastSavedAt(savedAt ?? null);

    window.setTimeout(() => {
      hydratingRef.current = false;
    }, 0);
  }, []);

  const parseSolutionContent = useCallback((content: string, path: string | null) => {
    const parsed = JSON.parse(content) as Partial<MapleStatsSolutionFile>;

    if (parsed.app !== 'MapleStats') {
      throw new Error('This file is not a MapleStats solution.');
    }

    const nextModuleId = isValidModuleId(parsed.workspace?.activeModuleId ?? '')
      ? parsed.workspace!.activeModuleId!
      : 'home';

    const title = typeof parsed.title === 'string' && parsed.title.trim()
      ? parsed.title.trim()
      : getSolutionNameFromPath(path);

    return createSolutionPayload(
      {
        activeModuleId: nextModuleId,
        activeWorkspaceTab: normalizeWorkspaceTab(parsed.workspace?.activeWorkspaceTab),
        dataInput: typeof parsed.workspace?.dataInput === 'string' ? parsed.workspace.dataInput : '',
        delimiterMode: typeof parsed.workspace?.delimiterMode === 'string' ? parsed.workspace.delimiterMode : 'auto',
        selectedColumnId: typeof parsed.workspace?.selectedColumnId === 'string' ? parsed.workspace.selectedColumnId : '',
        moduleSearch: typeof parsed.workspace?.moduleSearch === 'string' ? parsed.workspace.moduleSearch : '',
      },
      title,
    );
  }, []);

  const loadSolutionFromContent = useCallback((content: string, path: string | null, savedAt?: string | null) => {
    try {
      const payload = parseSolutionContent(content, path);
      applySolutionPayload(payload, path, savedAt);
      return true;
    } catch (error: any) {
      toast({
        title: 'Open failed',
        description: error?.message || 'This file could not be opened as a MapleStats solution.',
        variant: 'destructive',
      });
      return false;
    }
  }, [applySolutionPayload, parseSolutionContent, toast]);

  useEffect(() => {
    if (initialLoadRef.current) return;
    initialLoadRef.current = true;

    const loadInitialSolution = async () => {
      if (initialContent) {
        loadSolutionFromContent(initialContent, filePath ?? null);
        return;
      }

      if (filePath) {
        const content = await globalVFS.load(filePath);
        if (content) {
          loadSolutionFromContent(content, filePath);
        }
      }
    };

    loadInitialSolution();
  }, [filePath, initialContent, loadSolutionFromContent]);

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(MAPLESTATS_RECENT_SOLUTIONS_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        setRecentSolutionPaths(parsed.filter((value): value is string => typeof value === 'string'));
      }
    } catch {}
  }, []);

  useEffect(() => {
    if (!currentFilePath) return;
    pushRecentSolutionPath(currentFilePath);
  }, [currentFilePath, pushRecentSolutionPath]);

  const handleOpenModule = (moduleId: ModuleId) => {
    setActiveModuleId(moduleId);
    setActiveWorkspaceTab('solutions');
  };

  const handleAssistantRoute = useCallback((route: {
    moduleId: ModuleId;
    selectedColumnId?: string;
    workspaceTab?: WorkspaceTab;
  }) => {
    if (route.selectedColumnId) {
      setSelectedColumnId(route.selectedColumnId);
    }
    setActiveModuleId(route.moduleId);
    setActiveWorkspaceTab(route.workspaceTab ?? 'solutions');
  }, []);

  const resetSolution = useCallback(() => {
    hydratingRef.current = true;
    setActiveModuleId('home');
    setActiveWorkspaceTab('solutions');
    setDataInput('');
    setDelimiterMode('auto');
    setModuleSearch('');
    setSelectedColumnId('');
    setCurrentFilePath(null);
    setCurrentSolutionName(DEFAULT_SOLUTION_NAME);
    setLastSavedContent(null);
    setLastSavedAt(null);
    window.setTimeout(() => {
      hydratingRef.current = false;
    }, 0);
  }, []);

  const handleNewSolution = useCallback(() => {
    if (hasUnsavedChanges && !window.confirm('Discard your current MapleStats changes and start a new solution?')) {
      return;
    }
    resetSolution();
  }, [hasUnsavedChanges, resetSolution]);

  const handleLoadDemoData = useCallback(() => {
    setDataInput(DEMO_TEMPERATURE_DATA);
    setDelimiterMode('auto');
    setSelectedColumnId('');
    setActiveWorkspaceTab('data');
  }, []);

  const handleImportDataClick = useCallback(() => {
    importInputRef.current?.click();
  }, []);

  const handleImportDataFile = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const fileName = file.name.toLowerCase();
      let importedContent = '';

      if (fileName.endsWith('.csv') || fileName.endsWith('.txt')) {
        importedContent = await file.text();
      } else if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) {
        const buffer = await file.arrayBuffer();
        const workbook = XLSX.read(buffer, { type: 'array' });
        const firstSheetName = workbook.SheetNames[0];

        if (!firstSheetName) {
          throw new Error('The workbook does not contain any worksheets.');
        }

        importedContent = XLSX.utils.sheet_to_csv(workbook.Sheets[firstSheetName], {
          FS: ',',
          RS: '\n',
          blankrows: true,
        });
      } else {
        throw new Error('Please choose a CSV or Excel file.');
      }

      setDataInput(importedContent.replace(/\r\n/g, '\n').replace(/\r/g, '\n'));
      setDelimiterMode('auto');
      setSelectedColumnId('');
      setActiveWorkspaceTab('data');
    } catch (error: any) {

      toast({
        title: 'Import failed',
        description: error?.message || 'MapleStats could not import that file.',
        variant: 'destructive',
      });
    } finally {
      event.target.value = '';
    }
  }, [toast]);

  const ensureCloudDirectories = useCallback(async (directoryPath: string) => {
    const parent = normalizePath(directoryPath);
    if (!user || !parent) return;

    const parts = parent.split('\\').filter(Boolean);
    let current = parts[0] || '';

    for (let index = 1; index < parts.length; index += 1) {
      current = `${current}\\${parts[index]}`;
      if (!fileExists(current)) {
        await createFile(current, '', true, 'directory');
      }
    }

    if (!fileExists(parent)) {
      await createFile(parent, '', true, 'directory');
    }
  }, [createFile, fileExists, user]);

  const ensureLocalDirectories = useCallback(async (directoryPath: string) => {
    const parent = normalizePath(directoryPath);
    if (!parent) return;

    const parts = parent.split('\\').filter(Boolean);
    let current = parts[0] || '';

    for (let index = 1; index < parts.length; index += 1) {
      current = `${current}\\${parts[index]}`;
      try {
        await globalVFS.createDirectory(current);
      } catch {}
    }

    try {
      await globalVFS.createDirectory(parent);
    } catch {}
  }, []);

  const saveSolutionToPath = useCallback(async (targetPath: string, options?: { silent?: boolean }) => {
    const normalizedPath = ensureStatsExtension(targetPath);
    const nextTitle = getSolutionNameFromPath(normalizedPath);
    const payload = buildWorkspaceState(nextTitle);
    const serializedContent = JSON.stringify(payload, null, 2);
    const comparisonContent = createSolutionComparisonContent(payload.workspace, nextTitle);
    const parentPath = normalizedPath.includes('\\')
      ? normalizedPath.substring(0, normalizedPath.lastIndexOf('\\'))
      : '';

    if (!options?.silent) {
      setIsSaving(true);
    }

    try {
      if (parentPath) {
        await ensureLocalDirectories(parentPath);
        await ensureCloudDirectories(parentPath);
      }

      await globalVFS.save(normalizedPath, serializedContent);

      if (user) {
        const existing = getFile(normalizedPath);
        const success = existing || fileExists(normalizedPath)
          ? await updateFile(normalizedPath, serializedContent)
          : await createFile(normalizedPath, serializedContent, false, MAPLESTATS_CONTENT_TYPE);

        if (!success) {
          throw new Error('Cloud sync failed for this MapleStats solution.');
        }
      }

      setCurrentFilePath(normalizedPath);
      setCurrentSolutionName(nextTitle);
      setLastSavedContent(comparisonContent);
      setLastSavedAt(new Date().toLocaleString());

      return true;
    } catch (error: any) {
      if (!options?.silent) {
        toast({
          title: 'Save failed',
          description: error?.message || 'MapleStats could not save this solution.',
          variant: 'destructive',
        });
      }
      return false;
    } finally {
      if (!options?.silent) {
        setIsSaving(false);
      }
    }
  }, [buildWorkspaceState, createFile, ensureCloudDirectories, ensureLocalDirectories, fileExists, getFile, toast, updateFile, user]);

  const handleSave = useCallback(async () => {
    if (!currentFilePath) {
      setShowSaveAsDialog(true);
      return;
    }
    await saveSolutionToPath(currentFilePath);
  }, [currentFilePath, saveSolutionToPath]);

  const handleSaveAs = useCallback(async (fullPath: string) => {
    setShowSaveAsDialog(false);
    await saveSolutionToPath(fullPath);
  }, [saveSolutionToPath]);

  const handleOpenStoredSolution = useCallback(async (path: string) => {
    const freshestFile = await reloadFile(path);
    const file = freshestFile || getFile(path);
    const content = file?.content || await globalVFS.load(path);

    if (!content) {
      toast({
        title: 'Open failed',
        description: 'The selected MapleStats solution is empty or unavailable.',
        variant: 'destructive',
      });
      return;
    }

    const opened = loadSolutionFromContent(content, path, file?.updated_at ? new Date(file.updated_at).toLocaleString() : null);
    if (opened) {
      setShowOpenDialog(false);
      pushRecentSolutionPath(path);
    }
  }, [getFile, loadSolutionFromContent, reloadFile, toast, pushRecentSolutionPath]);

  const handleOpenMapleSheet = useCallback(async (path: string) => {
    const freshestFile = await reloadFile(path);
    const file = freshestFile || getFile(path);
    const content = file?.content || await globalVFS.load(path);

    if (!content) {
      toast({
        title: 'Open failed',
        description: 'The selected MapleSheet workbook is empty or unavailable.',
        variant: 'destructive',
      });
      return;
    }

    try {
      const worksheetContent = serializeMapleSheetToWorksheet(content);
      setDataInput(worksheetContent);
      setDelimiterMode('auto');
      setSelectedColumnId('');
      setActiveWorkspaceTab('data');
      setShowOpenSheetDialog(false);
    } catch (error: any) {
      toast({
        title: 'Open failed',
        description: error?.message || 'This file could not be opened as a MapleSheet workbook.',
        variant: 'destructive',
      });
    }
  }, [getFile, reloadFile, toast]);

  useEffect(() => {
    if (!currentFilePath || hydratingRef.current || !hasUnsavedChanges) return;

    const timer = window.setTimeout(async () => {
      setIsAutoSaving(true);
      await saveSolutionToPath(currentFilePath, { silent: true });
      setIsAutoSaving(false);
    }, 1500);

    return () => window.clearTimeout(timer);
  }, [currentFilePath, hasUnsavedChanges, saveSolutionToPath, currentSolutionContent]);

  const showHelp = useCallback(() => {
    setShowHelpDialog(true);
  }, []);

  const showAboutMapleStats = useCallback(() => {
    setShowAboutDialog(true);
  }, []);

  const renderSolutionsPanel = () => {
    switch (activeModuleId) {
      case 'home':
        return (
          <SolutionCenterPanel
            modules={MODULES}
            worksheet={worksheet}
            onOpenModule={handleOpenModule}
            onOpenData={() => setActiveWorkspaceTab('data')}
          />
        );
      case 'graph-builder':
        return <AdvancedGraphBuilder worksheet={worksheet} selectedColumnId={selectedColumn?.id} />;
      case 'descriptive':
        return <DescriptiveStats data={selectedData} />;
      case 'hypothesis':
        return <HypothesisTesting data={selectedData} />;
      case 'regression':
        return <RegressionAnalysis data={selectedData} />;
      case 'distributions':
        return <DistributionAnalysis data={selectedData} />;
      case 'visualization':
        return <DataVisualization data={selectedData} />;
      case 'capability-control':
        return <CapabilityControlCharts worksheet={worksheet} selectedColumnId={selectedColumn?.id} />;
      case 'doe-optimizer':
        return <DOEOptimizer worksheet={worksheet} />;
      case 'time-series':
        return <TimeSeriesForecasting worksheet={worksheet} selectedColumnId={selectedColumn?.id} />;
      case 'measurement-systems':
        return <MeasurementSystemsAnalysis worksheet={worksheet} selectedColumnId={selectedColumn?.id} />;
      case 'predictive-analytics':
        return <PredictiveAnalytics worksheet={worksheet} selectedColumnId={selectedColumn?.id} />;
      case 'multivariate':
        return <MultivariateAnalysis worksheet={worksheet} selectedColumnId={selectedColumn?.id} />;
      case 'reliability':
        return <ReliabilitySurvival worksheet={worksheet} selectedColumnId={selectedColumn?.id} />;
      case 'nonparametrics':
        return <NonparametricsAnalysis worksheet={worksheet} selectedColumnId={selectedColumn?.id} />;
      case 'tables':
        return <TablesCategoricalAnalysis worksheet={worksheet} selectedColumnId={selectedColumn?.id} />;
      default:
        return <PlannedModulePanel module={activeModule} />;
    }
  };

  const leftSidebar = (
    <aside
      className={`flex h-full min-h-0 flex-col overflow-hidden border-r border-border bg-card/80 transition-all duration-200 ${
        sidebarExpanded ? 'w-[26rem]' : 'w-20'
      }`}
    >

      <div className="border-b border-border p-3">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-12 w-12 rounded-2xl"
          onClick={() => setSidebarExpanded((value) => !value)}
          title={sidebarExpanded ? 'Collapse solutions menu' : 'Expand solutions menu'}
          aria-label={sidebarExpanded ? 'Collapse solutions menu' : 'Expand solutions menu'}
        >
          {sidebarExpanded ? <PanelLeftClose className="h-5 w-5" /> : <PanelLeftOpen className="h-5 w-5" />}
        </Button>
      </div>

      <ScrollArea className="min-h-0 flex-1">
        <div className="space-y-5 p-3">
          {Object.entries(groupedModules).map(([groupName, modules]) => (
            <div key={groupName} className="space-y-2">
              {sidebarExpanded ? (
                <div className="px-2 text-[11px] font-semibold uppercase tracking-[0.24em] text-muted-foreground">
                  {groupName}
                </div>
              ) : (
                <div className="mx-auto h-px w-8 bg-border" />
              )}

              {modules.map((module) => {
                const Icon = module.icon;
                const isActive = module.id === activeModuleId;
                const activeButtonClass = module.id === 'nonparametrics'
                  ? 'border-violet-700 bg-violet-700/10 shadow-sm'
                  : 'border-primary bg-primary/10 shadow-sm';
                const activeRailClass = module.id === 'nonparametrics' ? 'bg-violet-700' : 'bg-primary';

                return (
                  <button
                    key={module.id}
                    type="button"
                    onClick={() => handleOpenModule(module.id)}
                    title={module.name}
                    aria-label={module.name}
                    className={`group relative flex w-full min-w-0 items-center gap-3 rounded-2xl border text-left transition-all ${
                      sidebarExpanded ? 'px-3 py-3' : 'justify-center px-0 py-3'
                    } ${
                      isActive
                        ? activeButtonClass
                        : 'border-border bg-background hover:bg-muted/60'
                    }`}
                  >
                    {!sidebarExpanded && isActive ? (
                      <span className={`absolute left-0 top-1/2 h-8 w-1 -translate-y-1/2 rounded-r-full ${activeRailClass}`} />
                    ) : null}

                    <div className={`shrink-0 rounded-xl p-2 text-white ${module.accent}`}>
                      <Icon size={18} />
                    </div>

                    {sidebarExpanded ? (
                      <div className="min-w-0 flex-1">
                        <div className="mb-1 flex items-center gap-2">
                          <div className="truncate text-sm font-semibold text-foreground">{module.name}</div>
                          <Badge variant={getStatusBadgeVariant(module.status)} className="shrink-0 capitalize">
                            {module.status}
                          </Badge>
                        </div>
                        <p className="line-clamp-2 text-xs leading-relaxed text-muted-foreground">{module.description}</p>
                      </div>
                    ) : null}
                  </button>
                );
              })}
            </div>
          ))}

          {filteredModules.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border px-3 py-4 text-center text-xs text-muted-foreground">
              No solutions match your search.
            </div>
          ) : null}
        </div>
      </ScrollArea>
    </aside>
  );

  const centerWorkspace = (
    <main className="h-full min-h-0 overflow-hidden bg-background">
      <Tabs value={activeWorkspaceTab} onValueChange={(value) => setActiveWorkspaceTab(value as WorkspaceTab)} className="flex h-full min-h-0 flex-col">
        <div className="border-b border-border px-4 py-3">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex min-w-0 items-center gap-3">
              <TabsList className="grid w-fit grid-cols-2">
                <TabsTrigger value="solutions">Solutions</TabsTrigger>
                <TabsTrigger value="data">Data</TabsTrigger>
              </TabsList>

              <div className="hidden min-w-0 items-center gap-3 md:flex">
                <div className={`rounded-lg p-2 text-white ${activeModule.accent}`}>
                  <ActiveModuleIcon size={16} />
                </div>
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold text-foreground">{activeModule.name}</div>
                  <div className="truncate text-xs text-muted-foreground">{activeModule.group}</div>
                </div>
              </div>
            </div>

            <div className="relative w-full xl:max-w-sm">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={moduleSearch}
                onChange={(event) => setModuleSearch(event.target.value)}
                placeholder="Search solutions, methods, charts..."
                className="pl-9"
              />
            </div>
          </div>
        </div>

        <TabsContent value="solutions" className="mt-0 min-h-0 flex-1 overflow-hidden">
          <ScrollArea className="h-full">
            <div className="p-6">{renderSolutionsPanel()}</div>
          </ScrollArea>
        </TabsContent>

        <TabsContent value="data" className="mt-0 min-h-0 flex-1 overflow-hidden px-4 pb-4 pt-4">
          <div className="flex h-full min-h-0 flex-col gap-4">
            <div className="flex items-center justify-between rounded-xl border border-border bg-card px-4 py-3">
              <div>
                <div className="text-sm font-semibold text-foreground">Data Worksheet</div>
                <div className="text-xs text-muted-foreground">Enter your own data or load the demo worksheet here.</div>
              </div>
              <div className="flex items-center gap-2">
                <input
                  ref={importInputRef}
                  type="file"
                  accept=".csv,text/csv,.xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  className="hidden"
                  onChange={handleImportDataFile}
                />
                <Button variant="outline" size="sm" onClick={() => setShowOpenSheetDialog(true)}>
                  <FolderOpen className="mr-2 h-4 w-4" />
                  Open
                </Button>
                <Button variant="outline" size="sm" onClick={handleImportDataClick}>
                  <Upload className="mr-2 h-4 w-4" />
                  Import
                </Button>
                <Button size="sm" onClick={handleLoadDemoData}>
                  <FlaskConical className="mr-2 h-4 w-4" />
                  Demo
                </Button>
              </div>
            </div>

            <div className="min-h-0 flex-1">
              <MapleStatsDataSheet
                worksheet={worksheet}
                dataInput={dataInput}
                delimiterMode={delimiterMode}
                selectedColumnId={selectedColumn?.id}
                onDataInputChange={setDataInput}
                onDelimiterModeChange={setDelimiterMode}
                onSelectedColumnIdChange={setSelectedColumnId}
              />
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </main>
  );

  const statusText = isSaving
    ? 'Saving...'
    : isAutoSaving
      ? 'Auto-saving...'
      : hasUnsavedChanges
        ? 'Unsaved changes'
        : lastSavedAt
          ? `Saved ${lastSavedAt}`
          : 'New solution';

  return (
    <>
      <div className="relative flex h-full min-h-0 flex-col overflow-hidden bg-background text-foreground">
        <div className="border-b border-border bg-card text-card-foreground">
          <div className="flex items-center justify-between gap-4 px-4 py-3">
            <div className="flex min-w-0 items-center gap-3">
              <div className="rounded-md bg-primary/10 p-2 text-primary">
                <BarChart3 size={18} />
              </div>
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold">MapleStats — {currentSolutionName}.mstats</div>
                <div className="truncate text-xs text-muted-foreground">{statusText}</div>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="secondary" size="sm" onClick={handleSave} disabled={isSaving}>
                <Save className="mr-2 h-4 w-4" />
                Save
              </Button>
              {onClose ? <Button variant="secondary" size="sm" onClick={onClose}>Close</Button> : null}
            </div>

          </div>

          <div className="border-t border-border bg-muted/30 px-2 py-1">
            <Menubar className="h-8 rounded-none border-none bg-transparent text-foreground">

              <MenubarMenu>
                <MenubarTrigger className="h-8 px-3">File</MenubarTrigger>
                <MapleStatsMenubarContent>
                  <MenubarItem onClick={handleNewSolution}>
                    <RefreshCcw className="mr-2 h-4 w-4" />
                    New Solution
                  </MenubarItem>
                  <MenubarSub>
                    <MenubarSubTrigger>
                      <FolderOpen className="mr-2 h-4 w-4" />
                      Open
                    </MenubarSubTrigger>
                    <MapleStatsMenubarSubContent>
                      <MenubarItem onClick={() => setShowOpenDialog(true)}>
                        Browse solutions...
                      </MenubarItem>
                      <MenubarSeparator />
                      <MenubarLabel>Recent</MenubarLabel>
                      {recentSolutions.length > 0 ? (
                        recentSolutions.map((solution) => (
                          <MenubarItem key={solution.path} onClick={() => void handleOpenStoredSolution(solution.path)}>
                            <div className="flex min-w-0 flex-col">
                              <span className="truncate">{solution.title}</span>
                              <span className="truncate text-xs text-muted-foreground">{solution.path}</span>
                            </div>
                          </MenubarItem>
                        ))
                      ) : (
                        <MenubarItem disabled>No recent MapleStats solutions</MenubarItem>
                      )}
                    </MapleStatsMenubarSubContent>
                  </MenubarSub>
                  <MenubarSeparator />
                  <MenubarItem onClick={handleSave} disabled={isSaving}>
                    <Save className="mr-2 h-4 w-4" />
                    Save
                  </MenubarItem>
                  <MenubarItem onClick={() => setShowSaveAsDialog(true)} disabled={isSaving}>
                    <FileText className="mr-2 h-4 w-4" />
                    Save As...
                  </MenubarItem>
                </MapleStatsMenubarContent>
              </MenubarMenu>

              <MenubarMenu>
                <MenubarTrigger className="h-8 px-3">Data</MenubarTrigger>
                <MapleStatsMenubarContent>
                  <MenubarItem onClick={() => setActiveWorkspaceTab('data')}>
                    <Table2 className="mr-2 h-4 w-4" />
                    Open Worksheet
                  </MenubarItem>
                  <MenubarItem onClick={() => setDataInput('')}>
                    <LayoutGrid className="mr-2 h-4 w-4" />
                    Clear Worksheet
                  </MenubarItem>
                </MapleStatsMenubarContent>
              </MenubarMenu>

              <MenubarMenu>
                <MenubarTrigger className="h-8 px-3">Stat</MenubarTrigger>
                <MapleStatsMenubarContent>
                  <MenubarItem onClick={() => handleOpenModule('home')}>Solution Center</MenubarItem>
                  <MenubarItem onClick={() => handleOpenModule('graph-builder')}>Graph Builder</MenubarItem>
                  <MenubarItem onClick={() => handleOpenModule('descriptive')}>Descriptive Statistics</MenubarItem>
                  <MenubarItem onClick={() => handleOpenModule('hypothesis')}>Hypothesis Testing</MenubarItem>
                  <MenubarItem onClick={() => handleOpenModule('regression')}>Regression Analysis</MenubarItem>
                </MapleStatsMenubarContent>
              </MenubarMenu>

              <MenubarMenu>
                <MenubarTrigger className="h-8 px-3">Help</MenubarTrigger>
                <MapleStatsMenubarContent>
                  <MenubarItem onClick={showHelp}>
                    <HelpCircle className="mr-2 h-4 w-4" />
                    Quick Help
                  </MenubarItem>
                  <MenubarItem onClick={showAboutMapleStats}>
                    <Info className="mr-2 h-4 w-4" />
                    About MapleStats
                  </MenubarItem>
                </MapleStatsMenubarContent>
              </MenubarMenu>
            </Menubar>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-hidden">
          <div className="flex h-full min-h-0">
            {leftSidebar}
            <div className="min-w-0 flex-1">{centerWorkspace}</div>
          </div>
        </div>

        <MapleStatsAiAssistant
          worksheet={worksheet}
          onRoute={handleAssistantRoute}
        />
      </div>

      <Dialog open={showOpenDialog} onOpenChange={setShowOpenDialog}>
        <DialogContent className="z-[9999] sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Open MapleStats solution</DialogTitle>
            <DialogDescription>
              Open a saved <code className="rounded bg-muted px-1 py-0.5 text-xs">.mstats</code> solution from your cloud-backed MapleOS files.
            </DialogDescription>
          </DialogHeader>

          {!user ? (
            <div className="rounded-lg border border-dashed border-border p-4 text-sm text-muted-foreground">
              Sign in to browse your cloud-backed MapleStats solutions here. You can still open local <code className="rounded bg-muted px-1 py-0.5 text-xs">.mstats</code> files from File Explorer.
            </div>
          ) : !isLoaded ? (
            <div className="rounded-lg border border-dashed border-border p-4 text-sm text-muted-foreground">
              Loading your MapleStats solutions...
            </div>
          ) : availableSolutionFiles.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border p-4 text-sm text-muted-foreground">
              No MapleStats solutions found yet. Save your current work to create your first <code className="rounded bg-muted px-1 py-0.5 text-xs">.mstats</code> file.
            </div>
          ) : (
            <ScrollArea className="h-80 pr-4">
              <div className="space-y-3">
                {availableSolutionFiles.map((file) => (
                  <button
                    key={file.id}
                    type="button"
                    onClick={() => handleOpenStoredSolution(file.file_path)}
                    className="w-full rounded-xl border border-border bg-card p-4 text-left transition-colors hover:bg-muted/50"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="truncate font-semibold text-foreground">{file.file_name}</div>
                        <div className="mt-1 truncate text-xs text-muted-foreground">{file.file_path}</div>
                      </div>
                      <Badge variant="outline">.mstats</Badge>
                    </div>
                    <div className="mt-3 text-xs text-muted-foreground">
                      Updated {new Date(file.updated_at).toLocaleString()}
                    </div>
                  </button>
                ))}
              </div>
            </ScrollArea>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showOpenSheetDialog} onOpenChange={setShowOpenSheetDialog}>
        <DialogContent className="z-[9999] sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Open MapleSheet workbook</DialogTitle>
            <DialogDescription>
              Open a saved <code className="rounded bg-muted px-1 py-0.5 text-xs">.msheet</code> workbook and load its cells into the MapleStats Data worksheet.
            </DialogDescription>
          </DialogHeader>

          {!user ? (
            <div className="rounded-lg border border-dashed border-border p-4 text-sm text-muted-foreground">
              Sign in to browse your cloud-backed MapleSheet files here.
            </div>
          ) : !isLoaded ? (
            <div className="rounded-lg border border-dashed border-border p-4 text-sm text-muted-foreground">
              Loading your MapleSheet workbooks...
            </div>
          ) : availableMapleSheetFiles.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border p-4 text-sm text-muted-foreground">
              No MapleSheet workbooks found yet. Save a workbook as <code className="rounded bg-muted px-1 py-0.5 text-xs">.msheet</code> first, then open it here.
            </div>
          ) : (
            <ScrollArea className="h-80 pr-4">
              <div className="space-y-3">
                {availableMapleSheetFiles.map((file) => (
                  <button
                    key={file.id}
                    type="button"
                    onClick={() => handleOpenMapleSheet(file.file_path)}
                    className="w-full rounded-xl border border-border bg-card p-4 text-left transition-colors hover:bg-muted/50"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="truncate font-semibold text-foreground">{file.file_name}</div>
                        <div className="mt-1 truncate text-xs text-muted-foreground">{file.file_path}</div>
                      </div>
                      <Badge variant="outline">.msheet</Badge>
                    </div>
                    <div className="mt-3 text-xs text-muted-foreground">
                      Updated {new Date(file.updated_at).toLocaleString()}
                    </div>
                  </button>
                ))}
              </div>
            </ScrollArea>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showAboutDialog} onOpenChange={setShowAboutDialog}>
        <DialogContent className="z-[9999] sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>About MapleStats</DialogTitle>
            <DialogDescription>
              MapleStats brings worksheet-driven statistical analysis to MapleOS with modern charting, solution files, and cloud-aware storage.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="flex items-center gap-4 border-b border-border pb-4">
              <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-blue-600 text-white">
                <BarChart3 className="h-8 w-8" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">MapleStats</h2>
                <p className="text-sm text-muted-foreground">Statistical Analysis Workbench</p>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="font-medium">Version:</span>
                <span className="font-mono text-sm">1.0.0</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium">Edition:</span>
                <span className="text-sm">MapleOS Desktop</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium">Solution format:</span>
                <span className="font-mono text-sm">.mstats</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium">Cloud sync:</span>
                <span className="text-sm">Optional</span>
              </div>
            </div>

            <div className="space-y-3 border-t border-border pt-4">
              <h3 className="flex items-center gap-2 font-semibold">
                <Monitor className="h-4 w-4" />
                System Information
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Platform:</span>
                  <span className="font-mono">WASM x64</span>
                </div>
                <div className="flex justify-between">
                  <span>CPU Cores:</span>
                  <span className="font-mono">{navigator.hardwareConcurrency || 'Unknown'}</span>
                </div>
                <div className="flex justify-between">
                  <span>Device Memory:</span>
                  <span className="font-mono">{(navigator as any).deviceMemory ? `${(navigator as any).deviceMemory}GB` : 'Unknown'}</span>
                </div>
              </div>
            </div>

            <div className="space-y-3 border-t border-border pt-4">
              <h3 className="font-semibold">Included Features</h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="flex items-center gap-2">
                  <Sigma className="h-3 w-3" />
                  <span>Graph Builder</span>
                </div>
                <div className="flex items-center gap-2">
                  <BarChart3 className="h-3 w-3" />
                  <span>Descriptive stats</span>
                </div>
                <div className="flex items-center gap-2">
                  <TestTube className="h-3 w-3" />
                  <span>Hypothesis tests</span>
                </div>
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-3 w-3" />
                  <span>Regression analysis</span>
                </div>
                <div className="flex items-center gap-2">
                  <Gauge className="h-3 w-3" />
                  <span>Capability charts</span>
                </div>
                <div className="flex items-center gap-2">
                  <Cloud className="h-3 w-3" />
                  <span>Cloud copy support</span>
                </div>
              </div>
            </div>

            <div className="space-y-2 border-t border-border pt-4 text-center text-sm">
              <p className="font-medium">© 2026 MapleOS</p>
              <p className="text-muted-foreground">Built for worksheet-driven statistical analysis inside MapleOS.</p>
            </div>

            <div className="flex justify-end border-t border-border pt-4">
              <Button onClick={() => setShowAboutDialog(false)} size="sm">
                OK
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showHelpDialog} onOpenChange={setShowHelpDialog}>
        <DialogContent className="z-[9999] sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>MapleStats help</DialogTitle>
            <DialogDescription>
              Quick guidance for working in MapleStats without using toast notifications.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2 text-sm">
            <div className="rounded-lg border border-border/70 bg-muted/30 p-4">
              Start in the <span className="font-medium">Data</span> tab to paste, import, or open worksheet data.
            </div>
            <div className="rounded-lg border border-border/70 bg-muted/30 p-4">
              Use the <span className="font-medium">Stat</span> menu or the left sidebar to open descriptive stats, hypothesis tests, regression, forecasting, and other modules.
            </div>
            <div className="rounded-lg border border-border/70 bg-muted/30 p-4">
              Use the floating <span className="font-medium">MapleStats AI</span> assistant to ask for analyses like t-tests, regression, capability, MSA, or DOE.
            </div>
            <div className="flex justify-end border-t border-border pt-4">
              <Button onClick={() => setShowHelpDialog(false)} size="sm">
                OK
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <SaveAsDialog
        isOpen={showSaveAsDialog}
        onClose={() => setShowSaveAsDialog(false)}
        onSave={handleSaveAs}
        currentFileName={`${currentSolutionName}.mstats`}
        supportedExtensions={['mstats']}
      />
    </>
  );
};